import { useState } from "react";
import {
  Loader2,
  Mail,
  Save,
  Send,
  ServerCog,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import {
  useIssuers,
  useUpdateIssuerSmtp,
  useTestIssuerSmtp,
  type IssuerSmtpPayload,
} from "@modules/billing/api/issuers";
import type { Issuer } from "@modules/billing/types/issuer";

/**
 * Tabla de configuración SMTP por emisor. Cada razón social del holding envía
 * sus facturas desde su propio servidor de correo; el botón "enviar" de cada
 * factura usa el SMTP del emisor correspondiente.
 */
export function SmtpEmisoresTable() {
  const { data: issuers, isLoading } = useIssuers();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 p-4">
        <Loader2 className="animate-spin" size={18} /> Cargando emisores…
      </div>
    );
  }

  if (!issuers || issuers.length === 0) {
    return (
      <p className="text-sm text-slate-400 p-4">
        No hay emisores. Crea uno en Facturación · Emisores.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {issuers.map((emisor) => (
        <SmtpEmisorCard key={emisor.id} emisor={emisor} />
      ))}
    </div>
  );
}

function SmtpEmisorCard({ emisor }: { emisor: Issuer }) {
  const update = useUpdateIssuerSmtp();
  const test = useTestIssuerSmtp();

  const [form, setForm] = useState<IssuerSmtpPayload>({
    mail_transport: emisor.mail_transport ?? "smtp",
    mail_host: emisor.mail_host ?? "",
    mail_port: emisor.mail_port ?? 587,
    mail_username: emisor.mail_username ?? "",
    mail_password: "",
    mail_encryption: emisor.mail_encryption ?? "tls",
    mail_from_address: emisor.mail_from_address ?? "",
    mail_from_name: emisor.mail_from_name ?? "",
  });
  const isSes = form.mail_transport === "ses";
  const [testEmail, setTestEmail] = useState("");
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [configured, setConfigured] = useState(emisor.tiene_mail_config);
  const [passwordSet, setPasswordSet] = useState(emisor.tiene_mail_password);

  function set<K extends keyof IssuerSmtpPayload>(k: K, v: IssuerSmtpPayload[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function guardar() {
    setMsg(null);
    try {
      const payload: IssuerSmtpPayload = { ...form };
      // Si no se escribió contraseña nueva, no la enviamos (se conserva).
      if (!payload.mail_password) delete payload.mail_password;
      const updated = await update.mutateAsync({ id: emisor.id, payload });
      setConfigured(updated.tiene_mail_config);
      setPasswordSet(updated.tiene_mail_password);
      setForm((f) => ({ ...f, mail_password: "" }));
      setMsg({ kind: "ok", text: "Configuración SMTP guardada." });
    } catch (e) {
      setMsg({
        kind: "err",
        text:
          (e as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? "No se pudo guardar.",
      });
    }
  }

  async function probar() {
    setMsg(null);
    const email = testEmail.trim();
    if (!email) {
      setMsg({ kind: "err", text: "Escribe un email de destino para la prueba." });
      return;
    }
    try {
      const res = await test.mutateAsync({ id: emisor.id, email });
      if (res.ok) {
        setMsg({ kind: "ok", text: `Email de prueba enviado a ${res.email}.` });
      } else {
        setMsg({ kind: "err", text: res.message ?? "No se pudo enviar." });
      }
    } catch (e) {
      setMsg({
        kind: "err",
        text:
          (e as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? "No se pudo enviar el email de prueba.",
      });
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-soft">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-brand-100 text-brand-700 shrink-0">
            <ServerCog size={20} />
          </div>
          <div className="min-w-0">
            <div className="font-black text-slate-900 truncate">{emisor.nombre}</div>
            <div className="text-xs text-slate-400 truncate">{emisor.cif ?? "—"}</div>
          </div>
        </div>
        {configured ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-xs font-bold whitespace-nowrap">
            <CheckCircle2 size={13} /> SMTP configurado
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-3 py-1 text-xs font-bold whitespace-nowrap">
            <AlertCircle size={13} /> Sin configurar
          </span>
        )}
      </div>

      {/* Método de envío */}
      <div className="mb-3">
        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Método de envío</span>
        <div className="mt-1 flex gap-2">
          <button
            type="button"
            onClick={() => set("mail_transport", "smtp")}
            className={`rounded-lg px-3 py-2 text-sm font-bold border transition ${
              !isSes ? "bg-brand-700 text-white border-brand-700" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
          >
            SMTP propio
          </button>
          <button
            type="button"
            onClick={() => set("mail_transport", "ses")}
            className={`rounded-lg px-3 py-2 text-sm font-bold border transition ${
              isSes ? "bg-brand-700 text-white border-brand-700" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
          >
            Amazon SES (API)
          </button>
        </div>
        {isSes && (
          <p className="mt-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            Envía vía Amazon SES por API (HTTPS), usando las credenciales AWS globales del sistema.
            No necesita host/puerto/contraseña. El <strong>remitente debe ser una identidad verificada</strong> en SES.
          </p>
        )}
      </div>

      <div className={`grid gap-3 sm:grid-cols-2 lg:grid-cols-3 ${isSes ? "hidden" : ""}`}>
        <Field label="Servidor SMTP (host)">
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="smtp.office365.com"
            value={form.mail_host ?? ""}
            onChange={(e) => set("mail_host", e.target.value)}
          />
        </Field>
        <Field label="Puerto">
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            type="number"
            placeholder="587"
            value={form.mail_port ?? ""}
            onChange={(e) => set("mail_port", e.target.value ? Number(e.target.value) : null)}
          />
        </Field>
        <Field label="Cifrado">
          <select
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={form.mail_encryption ?? ""}
            onChange={(e) =>
              set("mail_encryption", (e.target.value || null) as "tls" | "ssl" | null)
            }
          >
            <option value="tls">TLS</option>
            <option value="ssl">SSL</option>
            <option value="">Ninguno</option>
          </select>
        </Field>
        <Field label="Usuario">
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="facturacion@empresa.com"
            value={form.mail_username ?? ""}
            onChange={(e) => set("mail_username", e.target.value)}
          />
        </Field>
        <Field label="Contraseña">
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            type="password"
            placeholder={passwordSet ? "•••••••• (sin cambios)" : "Contraseña SMTP"}
            value={form.mail_password ?? ""}
            onChange={(e) => set("mail_password", e.target.value)}
          />
        </Field>
      </div>

      {/* Remitente: aplica a ambos métodos (para SES debe estar verificado) */}
      <div className="grid gap-3 sm:grid-cols-2 mt-3">
        <Field label={isSes ? "Remitente (email verificado en SES)" : "Remitente (email)"}>
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="facturacion@empresa.com"
            value={form.mail_from_address ?? ""}
            onChange={(e) => set("mail_from_address", e.target.value)}
          />
        </Field>
        <Field label="Remitente (nombre)">
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder={emisor.nombre}
            value={form.mail_from_name ?? ""}
            onChange={(e) => set("mail_from_name", e.target.value)}
          />
        </Field>
      </div>

      {msg && (
        <div
          className={`mt-3 rounded-lg px-3 py-2 text-sm border ${
            msg.kind === "ok"
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-rose-50 border-rose-200 text-rose-700"
          }`}
        >
          {msg.text}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
        <button
          onClick={guardar}
          disabled={update.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
        >
          {update.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Guardar
        </button>

        <div className="flex items-center gap-2 ml-auto">
          <div className="relative">
            <Mail
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              className="w-56 rounded-lg border border-slate-300 pl-8 pr-3 py-2 text-sm"
              type="email"
              placeholder="email para la prueba"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
          </div>
          <button
            onClick={probar}
            disabled={test.isPending}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 hover:bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700 disabled:opacity-50"
          >
            {test.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Probar envío
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
